import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp,
  Assessment,
  People,
  EventAvailable,
  Comment,
  CheckCircle,
  Cancel,
  Edit,
  Email,
  Phone,
  LinkedIn,
} from '@mui/icons-material';
import { useAuth } from '../lib/supabase/AuthContext';
import {
  getAllRdvWithDetails,
  getAdminStats,
  getClientStats,
  getCallerStats,
  getCampaignComments,
  addCampaignComment,
  updateCommentResolution,
} from '../lib/supabase/adminService';
import {
  getAllCampaigns,
  updateRdv,
} from '../lib/supabase/callerService';
import {
  AdminStatsData,
  ClientStats,
  CallerStats,
  RdvWithDetails,
  CampaignComment,
  RdvFormData,
} from '../types/admin';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

export const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // États pour les données
  const [adminStats, setAdminStats] = useState<AdminStatsData | null>(null);
  const [rdvList, setRdvList] = useState<RdvWithDetails[]>([]);
  const [clientStats, setClientStats] = useState<ClientStats[]>([]);
  const [callerStats, setCallerStats] = useState<CallerStats[]>([]);
  const [comments, setComments] = useState<CampaignComment[]>([]);

  // États pour les filtres et recherche
  const [rdvFilter, setRdvFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [callerPeriodFilter, setCallerPeriodFilter] = useState<string>('all');

  // États pour les commentaires
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [newComment, setNewComment] = useState('');

  // États pour la modale retour client
  const [retourClientDialogOpen, setRetourClientDialogOpen] = useState(false);
  const [currentRetourClient, setCurrentRetourClient] = useState<string>('');

  // États pour la modale de modification RDV
  const [rdvDialogOpen, setRdvDialogOpen] = useState(false);
  const [selectedRdv, setSelectedRdv] = useState<RdvWithDetails | null>(null);
  const [rdvForm, setRdvForm] = useState<RdvFormData>({
    prenom: '',
    nom: '',
    entreprise: '',
    email: '',
    telephone: '',
    rdv_date: '',
    rdv_commentaire: '',
    rdv_source: 'Phone',
    campaign_id: '',
    etat_rdv: 'En attente',
    prospection_etape: 'RDV planifié',
  });
  const [campaigns, setCampaigns] = useState<any[]>([]);

  // Charger les données
  useEffect(() => {
    loadAllData();
  }, []);

  // Recharger les stats callers quand le filtre change
  useEffect(() => {
    if (callerPeriodFilter) {
      loadCallerStats();
    }
  }, [callerPeriodFilter]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsResult, rdvResult, clientResult, callerResult, commentsResult, campaignsResult] = await Promise.all([
        getAdminStats(),
        getAllRdvWithDetails(),
        getClientStats(),
        getCallerStats(callerPeriodFilter as 'all' | 'current_month' | 'current_week'),
        getCampaignComments(),
        getAllCampaigns(),
      ]);

      setAdminStats(statsResult);
      setRdvList(rdvResult || []);
      setClientStats(clientResult || []);
      setCallerStats(callerResult || []);
      setComments(commentsResult || []);
      setCampaigns(campaignsResult || []);
    } catch (err: any) {
      console.error('Erreur lors du chargement des données admin:', err);
      setError(`Erreur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadCallerStats = async () => {
    try {
      const callerResult = await getCallerStats(callerPeriodFilter as 'all' | 'current_month' | 'current_week');
      setCallerStats(callerResult || []);
    } catch (err: any) {
      console.error('Erreur lors du chargement des stats callers:', err);
      setError(`Erreur: ${err.message}`);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Filtrer les RDV
  const filteredRdv = rdvList.filter(rdv => {
    const matchesFilter = rdvFilter === 'all' || rdv.etat_rdv === rdvFilter;
    const matchesSearch = 
      rdv.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rdv.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rdv.entreprise.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rdv.campaign_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rdv.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  // Ajouter un commentaire
  const handleAddComment = async () => {
    if (!selectedCampaign || !newComment.trim() || !user) return;

    try {
      await addCampaignComment(
        selectedCampaign.id,
        user.id,
        user.user_metadata?.full_name || user.email || 'Admin',
        newComment
      );

      setNewComment('');
      setCommentDialogOpen(false);
      setSelectedCampaign(null);
      
      // Recharger les commentaires
      const updatedComments = await getCampaignComments();
      setComments(updatedComments);
    } catch (err: any) {
      console.error('Erreur lors de l\'ajout du commentaire:', err);
      setError(`Erreur: ${err.message}`);
    }
  };

  // Mettre à jour le statut de résolution d'un commentaire
  const handleToggleResolution = async (commentId: string, isResolved: boolean) => {
    try {
      await updateCommentResolution(commentId, !isResolved);

      // Mettre à jour localement
      setComments(prev => 
        prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, is_resolved: !isResolved }
            : comment
        )
      );
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour du commentaire:', err);
      setError(`Erreur: ${err.message}`);
    }
  };

  // Fonction pour ouvrir la modale retour client
  const handleViewRetourClient = (retourClient: string | null) => {
    setCurrentRetourClient(retourClient || 'Aucun retour client');
    setRetourClientDialogOpen(true);
  };

  // Fonctions pour gérer la modification des RDV
  const handleEditRdv = (rdv: RdvWithDetails) => {
    setRdvForm({
      prenom: rdv.prenom,
      nom: rdv.nom,
      entreprise: rdv.entreprise,
      email: rdv.email || '',
      telephone: rdv.telephone || '',
      rdv_date: rdv.rdv_date,
      rdv_commentaire: rdv.rdv_commentaire || '',
      rdv_source: rdv.rdv_source,
      campaign_id: rdv.campaign_id,
      etat_rdv: rdv.etat_rdv,
      prospection_etape: rdv.prospection_etape,
    });
    setSelectedRdv(rdv);
    setRdvDialogOpen(true);
  };

  const handleRdvFormChange = (field: keyof RdvFormData, value: string) => {
    setRdvForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveRdv = async () => {
    if (!selectedRdv) return;

    try {
      await updateRdv(selectedRdv.id, rdvForm);
      setRdvDialogOpen(false);
      setSelectedRdv(null);
      
      // Recharger les RDV
      const updatedRdv = await getAllRdvWithDetails();
      setRdvList(updatedRdv);
    } catch (err: any) {
      console.error('Erreur lors de la modification du RDV:', err);
      setError(`Erreur: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        🔧 Administration Growlyy
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Vue d'ensemble" icon={<Assessment />} />
          <Tab label="Tous les RDV" icon={<EventAvailable />} />
          <Tab label="Stats Clients" icon={<People />} />
          <Tab label="Stats Callers" icon={<TrendingUp />} />
          <Tab label="Commentaires" icon={<Comment />} />
        </Tabs>
      </Box>

      {/* Tab 1: Vue d'ensemble */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total RDV
                </Typography>
                <Typography variant="h4">
                  {adminStats?.totalRdv || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  RDV Validés
                </Typography>
                <Typography variant="h4" color="success.main">
                  {adminStats?.rdvValidated || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Taux de Conversion
                </Typography>
                <Typography variant="h4" color="primary">
                  {adminStats?.conversionRate?.toFixed(1) || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  En Attente
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {adminStats?.rdvPending || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Graphiques et résumés récents */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Répartition des RDV
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Chip label={`Validés: ${adminStats?.rdvValidated || 0}`} color="success" sx={{ mr: 1, mb: 1 }} />
                  <Chip label={`En attente: ${adminStats?.rdvPending || 0}`} color="warning" sx={{ mr: 1, mb: 1 }} />
                  <Chip label={`Litige: ${adminStats?.rdvLitige || 0}`} color="error" sx={{ mr: 1, mb: 1 }} />
                  <Chip label={`No show: ${adminStats?.rdvNoShow || 0}`} color="default" sx={{ mr: 1, mb: 1 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Activité récente
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {clientStats.length} clients actifs
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {callerStats.length} callers actifs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 2: Tous les RDV */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Rechercher"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={rdvFilter}
              label="Statut"
              onChange={(e) => setRdvFilter(e.target.value)}
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="En attente">En attente</MenuItem>
              <MenuItem value="Validé">Validé</MenuItem>
              <MenuItem value="Litige">Litige</MenuItem>
              <MenuItem value="No show">No show</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date RDV</TableCell>
                <TableCell>Prospect</TableCell>
                <TableCell>Entreprise</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Campagne</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Retour client</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRdv.map((rdv) => (
                <TableRow key={rdv.id}>
                  <TableCell>
                    {new Date(rdv.rdv_date).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {rdv.prenom} {rdv.nom}
                    </Typography>
                  </TableCell>
                  <TableCell>{rdv.entreprise}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {rdv.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Email fontSize="small" />
                          {rdv.email}
                        </Box>
                      )}
                      {rdv.telephone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone fontSize="small" />
                          {rdv.telephone}
                        </Box>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {rdv.campaign_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {rdv.client_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rdv.rdv_source}
                      size="small"
                      icon={
                        rdv.rdv_source === 'Mail' ? <Email /> :
                        rdv.rdv_source === 'LinkedIn' ? <LinkedIn /> : <Phone />
                      }
                      color={
                        rdv.rdv_source === 'Mail' ? 'primary' :
                        rdv.rdv_source === 'LinkedIn' ? 'info' : 'secondary'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rdv.etat_rdv}
                      color={
                        rdv.etat_rdv === 'Validé' ? 'success' :
                        rdv.etat_rdv === 'En attente' ? 'warning' :
                        rdv.etat_rdv === 'Litige' ? 'error' : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {rdv.retour_client ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewRetourClient(rdv.retour_client)}
                      >
                        Voir
                      </Button>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Aucun retour
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Modifier">
                      <IconButton size="small" onClick={() => handleEditRdv(rdv)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab 3: Stats Clients */}
      <TabPanel value={tabValue} index={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Campagnes</TableCell>
                <TableCell>RDV Total</TableCell>
                <TableCell>RDV Validés</TableCell>
                <TableCell>No show</TableCell>
                <TableCell>Litige</TableCell>
                <TableCell>En attente</TableCell>
                <TableCell>Taux Conversion</TableCell>
                <TableCell>Dernier RDV</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientStats.map((client) => (
                <TableRow key={client.client_id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {client.client_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{client.client_email}</TableCell>
                  <TableCell>
                    {client.totalCampaigns} ({client.activeCampaigns} actives)
                  </TableCell>
                  <TableCell>{client.totalRdv}</TableCell>
                  <TableCell>
                    <Typography color="success.main">
                      {client.rdvValidated}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="text.secondary">
                      {client.rdvNoShow}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="error.main">
                      {client.rdvLitige}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="warning.main">
                      {client.rdvPending}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="primary">
                      {client.conversionRate.toFixed(1)}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {client.lastRdvDate ? 
                      new Date(client.lastRdvDate).toLocaleDateString('fr-FR') : 
                      'Aucun RDV'
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab 4: Stats Callers */}
      <TabPanel value={tabValue} index={3}>
        <Box sx={{ mb: 3 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Période</InputLabel>
            <Select
              value={callerPeriodFilter}
              label="Période"
              onChange={(e) => setCallerPeriodFilter(e.target.value)}
            >
              <MenuItem value="all">Tout temps</MenuItem>
              <MenuItem value="current_month">Mois en cours</MenuItem>
              <MenuItem value="current_week">Semaine en cours</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Caller</TableCell>
                <TableCell>RDV Générés</TableCell>
                <TableCell>RDV Validé</TableCell>
                <TableCell>No show</TableCell>
                <TableCell>Litige</TableCell>
                <TableCell>En attente</TableCell>
                <TableCell>Taux Validation</TableCell>
                <TableCell>Moyenne/Jour</TableCell>
                <TableCell>Date dernier RDV</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {callerStats.map((caller) => (
                <TableRow key={caller.caller_id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {caller.caller_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{caller.totalRdvGenerated}</TableCell>
                  <TableCell>
                    <Typography color="success.main">
                      {caller.rdvValidated}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="text.secondary">
                      {caller.rdvNoShow}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="error.main">
                      {caller.rdvLitige}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="warning.main">
                      {caller.rdvPending}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="primary">
                      {caller.validationRate.toFixed(1)}%
                    </Typography>
                  </TableCell>
                  <TableCell>{caller.avgRdvPerDay.toFixed(1)}</TableCell>
                  <TableCell>
                    {caller.lastRdvDate ? 
                      new Date(caller.lastRdvDate).toLocaleDateString('fr-FR') : 
                      'Aucun RDV'
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab 5: Commentaires */}
      <TabPanel value={tabValue} index={4}>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<Comment />}
            onClick={() => setCommentDialogOpen(true)}
          >
            Nouveau Commentaire
          </Button>
        </Box>

        <Grid container spacing={2}>
          {comments.map((comment) => (
            <Grid item xs={12} key={comment.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">
                        {comment.campaign_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Par {comment.author_name} • {new Date(comment.created_at).toLocaleDateString('fr-FR')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={comment.is_resolved ? 'Résolu' : 'En cours'}
                        color={comment.is_resolved ? 'success' : 'warning'}
                        size="small"
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleToggleResolution(comment.id, comment.is_resolved)}
                      >
                        {comment.is_resolved ? <Cancel /> : <CheckCircle />}
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {comment.text}
                  </Typography>
                  {comment.responses && comment.responses.length > 0 && (
                    <Box sx={{ ml: 2, borderLeft: '2px solid #e0e0e0', pl: 2 }}>
                      {comment.responses.map((response) => (
                        <Box key={response.id} sx={{ mb: 1 }}>
                          <Typography variant="body2" color="textSecondary">
                            {response.author_name} • {new Date(response.created_at).toLocaleDateString('fr-FR')}
                          </Typography>
                          <Typography variant="body2">
                            {response.text}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Dialog pour ajouter un commentaire */}
      <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Ajouter un commentaire
          {selectedCampaign && (
            <Typography variant="body2" color="textSecondary">
              Campagne: {selectedCampaign.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Votre commentaire..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)}>
            Annuler
          </Button>
          <Button variant="contained" onClick={handleAddComment} disabled={!newComment.trim()}>
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour afficher le retour client */}
      <Dialog 
        open={retourClientDialogOpen} 
        onClose={() => setRetourClientDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Retour Client
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
            {currentRetourClient}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetourClientDialogOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour modifier un RDV */}
      <Dialog open={rdvDialogOpen} onClose={() => setRdvDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Modifier le RDV
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prénom *"
                value={rdvForm.prenom}
                onChange={(e) => handleRdvFormChange('prenom', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom *"
                value={rdvForm.nom}
                onChange={(e) => handleRdvFormChange('nom', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Entreprise *"
                value={rdvForm.entreprise}
                onChange={(e) => handleRdvFormChange('entreprise', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={rdvForm.email}
                onChange={(e) => handleRdvFormChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={rdvForm.telephone}
                onChange={(e) => handleRdvFormChange('telephone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date et heure du RDV *"
                type="datetime-local"
                value={rdvForm.rdv_date}
                onChange={(e) => handleRdvFormChange('rdv_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Source *</InputLabel>
                <Select
                  value={rdvForm.rdv_source}
                  label="Source *"
                  onChange={(e) => handleRdvFormChange('rdv_source', e.target.value)}
                >
                  <MenuItem value="Phone">Téléphone</MenuItem>
                  <MenuItem value="Mail">Email</MenuItem>
                  <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Statut *</InputLabel>
                <Select
                  value={rdvForm.etat_rdv}
                  label="Statut *"
                  onChange={(e) => handleRdvFormChange('etat_rdv', e.target.value)}
                >
                  <MenuItem value="En attente">En attente</MenuItem>
                  <MenuItem value="Validé">Validé</MenuItem>
                  <MenuItem value="Litige">Litige</MenuItem>
                  <MenuItem value="No show">No show</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Campagne *</InputLabel>
                <Select
                  value={rdvForm.campaign_id}
                  label="Campagne *"
                  onChange={(e) => handleRdvFormChange('campaign_id', e.target.value)}
                >
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.nom} {!campaign.lance ? '(Inactive)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Commentaire"
                value={rdvForm.rdv_commentaire}
                onChange={(e) => handleRdvFormChange('rdv_commentaire', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRdvDialogOpen(false)}>
            Annuler
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveRdv}
            disabled={!rdvForm.prenom || !rdvForm.nom || !rdvForm.entreprise || !rdvForm.rdv_date || !rdvForm.campaign_id}
          >
            Modifier
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}; 